import { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import { api, apiError, streamAssistant } from "@/lib/api";
import { useParentScope } from "@/context/ParentContext";
import { EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ContextualNav } from "@/components/ContextualNav";

const SUGGESTIONS = [
  "¿Que deberia repasar hoy?",
  "¿En que se esta equivocando mas?",
  "¿Esta mejorando en Ciencias Naturales?",
  "¿Como va su preparacion para el proximo examen?",
];

export default function ParentAssistant() {
  const { student } = useParentScope();
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    if (!student) return;
    api
      .get("/assistant/history", { params: { student_id: student.id } })
      .then((res) => setMessages(res.data))
      .catch(() => setMessages([]));
  }, [student]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const ask = async (text) => {
    if (!text.trim() || busy || !student) return;
    setError("");
    setBusy(true);
    setQuestion("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      await streamAssistant({
        studentId: student.id,
        question: text,
        onDelta: (delta) =>
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + delta };
            return next;
          }),
      });
    } catch (err) {
      setError(err.message || apiError(err));
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  if (!student) return <EmptyState title="Selecciona un hijo" description="Crea primero un perfil de estudiante." />;

  return (
    <div className="space-y-5" data-testid="parent-assistant-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight text-slate-900">
          <Sparkles className="h-7 w-7 text-violet-600" /> Asistente IA
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Responde solo con los datos reales de {student.first_name}. Si no hay evidencia suficiente, te lo dira.
        </p>
      </div>

      <div className="estudia-card flex min-h-[420px] flex-col p-5">
        <div className="flex-1 space-y-4 overflow-y-auto" data-testid="assistant-messages">
          {messages.length === 0 && (
            <p className="text-sm text-slate-500">
              Preguntame sobre su progreso, sus dificultades o que conviene repasar.
            </p>
          )}
          {messages.map((message, index) => (
            <div
              key={index}
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                message.role === "user"
                  ? "ml-auto bg-blue-600 text-white"
                  : "bg-slate-50 text-slate-800"
              }`}
              data-testid={`assistant-message-${message.role}`}
            >
              {message.content || (busy ? "Analizando sus datos..." : "")}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {error && (
          <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="assistant-error">
            {error}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => ask(suggestion)}
              disabled={busy}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-blue-300 hover:text-blue-700 disabled:opacity-50"
              data-testid="assistant-suggestion"
            >
              {suggestion}
            </button>
          ))}
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            ask(question);
          }}
          className="mt-3 flex gap-2"
          data-testid="assistant-form"
        >
          <Input
            className="estudia-touch"
            placeholder="Escribe tu pregunta..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            data-testid="assistant-input"
          />
          <Button
            type="submit"
            aria-label="Enviar pregunta"
            disabled={busy || !question.trim()}
            className="estudia-touch rounded-xl bg-violet-600 font-semibold hover:bg-violet-700"
            data-testid="assistant-send-btn"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>

      <ContextualNav
        eyebrow="Consulta la fuente"
        title="Revisa los datos detrás de las respuestas"
        items={[
          { to: "/p", icon: "dashboard", label: "Ver progreso", description: "Sesiones, dominio y próximos exámenes." },
          { to: "/p/material", icon: "material", label: "Revisar material", description: "Apuntes, procesamiento y ejercicios." },
          { to: "/p/calendario", icon: "calendar", label: "Abrir calendario", description: "Materias, fechas y notas reales." },
        ]}
      />
    </div>
  );
}
